import React, { useEffect } from 'react';

const ChainOfResponsibilityPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Chain of Responsibility Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Chain of Responsibility Pattern - pass requests along a chain of handlers until one handles it. Learn with logging and authentication examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Chain of Responsibility Pattern - pass requests along a chain of handlers until one handles it. Learn with logging and authentication examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'chain of responsibility pattern, design patterns, behavioral patterns, request handling, middleware, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'chain of responsibility pattern, design patterns, behavioral patterns, request handling, middleware, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Chain of Responsibility Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Pass requests along a chain of handlers
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Chain of Responsibility pattern allows you to pass requests along a chain of handlers. 
            Upon receiving a request, each handler decides either to process the request or to pass it 
            to the next handler in the chain. This decouples senders from receivers.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to process different types of requests in various ways, but you don't want to 
            couple the request sender to specific handlers. You want the flexibility to change the 
            order of handlers or add new ones without modifying existing code.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Support Ticket System</h2>
          <div style={styles.codeBlock}>
{`// Handler interface
abstract class SupportHandler {
    protected SupportHandler nextHandler;
    
    public void setNext(SupportHandler handler) {
        this.nextHandler = handler;
    }
    
    public abstract void handleRequest(SupportTicket ticket);
}

// Request object
class SupportTicket {
    private String issue;
    private Priority priority;
    private String customerType;
    
    public SupportTicket(String issue, Priority priority, String customerType) {
        this.issue = issue;
        this.priority = priority;
        this.customerType = customerType;
    }
    
    // Getters
    public String getIssue() { return issue; }
    public Priority getPriority() { return priority; }
    public String getCustomerType() { return customerType; }
    
    enum Priority { LOW, MEDIUM, HIGH, CRITICAL }
}

// Concrete handlers
class Level1Support extends SupportHandler {
    @Override
    public void handleRequest(SupportTicket ticket) {
        if (ticket.getPriority() == SupportTicket.Priority.LOW) {
            System.out.println("Level 1 Support: Handling basic issue - " + ticket.getIssue());
            System.out.println("Resolution: Provided standard troubleshooting steps");
        } else if (nextHandler != null) {
            System.out.println("Level 1 Support: Escalating to Level 2");
            nextHandler.handleRequest(ticket);
        } else {
            System.out.println("Level 1 Support: No handler available for this request");
        }
    }
}

class Level2Support extends SupportHandler {
    @Override
    public void handleRequest(SupportTicket ticket) {
        if (ticket.getPriority() == SupportTicket.Priority.MEDIUM) {
            System.out.println("Level 2 Support: Handling technical issue - " + ticket.getIssue());
            System.out.println("Resolution: Applied technical fix and provided detailed explanation");
        } else if (nextHandler != null) {
            System.out.println("Level 2 Support: Escalating to Level 3");
            nextHandler.handleRequest(ticket);
        } else {
            System.out.println("Level 2 Support: No handler available for this request");
        }
    }
}

class Level3Support extends SupportHandler {
    @Override
    public void handleRequest(SupportTicket ticket) {
        if (ticket.getPriority() == SupportTicket.Priority.HIGH) {
            System.out.println("Level 3 Support: Handling complex issue - " + ticket.getIssue());
            System.out.println("Resolution: Expert analysis and custom solution provided");
        } else if (nextHandler != null) {
            System.out.println("Level 3 Support: Escalating to Management");
            nextHandler.handleRequest(ticket);
        } else {
            System.out.println("Level 3 Support: No handler available for this request");
        }
    }
}

class ManagementSupport extends SupportHandler {
    @Override
    public void handleRequest(SupportTicket ticket) {
        if (ticket.getPriority() == SupportTicket.Priority.CRITICAL) {
            System.out.println("Management: Handling critical issue - " + ticket.getIssue());
            System.out.println("Resolution: Direct management intervention and immediate action");
        } else {
            System.out.println("Management: Issue doesn't require management intervention");
        }
    }
}

// Usage
public class ChainOfResponsibilityDemo {
    public static void main(String[] args) {
        // Create handlers
        SupportHandler level1 = new Level1Support();
        SupportHandler level2 = new Level2Support();
        SupportHandler level3 = new Level3Support();
        SupportHandler management = new ManagementSupport();
        
        // Build the chain
        level1.setNext(level2);
        level2.setNext(level3);
        level3.setNext(management);
        
        // Create tickets
        SupportTicket[] tickets = {
            new SupportTicket("Password reset", SupportTicket.Priority.LOW, "Regular"),
            new SupportTicket("Software installation issue", SupportTicket.Priority.MEDIUM, "Business"),
            new SupportTicket("Server performance problem", SupportTicket.Priority.HIGH, "Enterprise"),
            new SupportTicket("System-wide outage", SupportTicket.Priority.CRITICAL, "Enterprise")
        };
        
        // Process tickets
        for (SupportTicket ticket : tickets) {
            System.out.println("\\n--- Processing Ticket ---");
            System.out.println("Issue: " + ticket.getIssue());
            System.out.println("Priority: " + ticket.getPriority());
            level1.handleRequest(ticket);
        }
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Decoupling:</strong> Sender doesn't need to know which handler will process the request</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Can add, remove, or reorder handlers dynamically</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Each handler focuses on one type of request</li>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Easy to add new handlers without modifying existing ones</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you have multiple objects that can handle a request</li>
            <li style={styles.listItem}>When you want to decouple request sender from receiver</li>
            <li style={styles.listItem}>When the set of handlers should be specified dynamically</li>
            <li style={styles.listItem}>When you need to process requests in a specific order</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Chain of Responsibility pattern is excellent for creating flexible request processing 
            systems. It's commonly used in middleware systems, event handling, and validation 
            frameworks where multiple handlers might process a single request.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ChainOfResponsibilityPattern;
